import { Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, filter } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../shared/services/project.service';
import { UserService } from '../../shared/services/user.service';
import { User } from '../../shared/services/user';
import { TaskService } from '../../shared/services/task.service';
import { ExternalLinkService } from '../../shared/services/external-link.service';
import { ClientService, Client as ClientModel } from '../../shared/services/client.service';
import { TimeEntriesService } from '../../shared/services/time-entries.service';
import { Project, ProjectStage } from '../../shared/models/project.model';
import { User as UserModel } from '../../shared/models/user.model';
import { Task } from '../../shared/models/task.model';
import { AddStageProjectComponent } from '../projects/display-projects/add-stage-project/add-stage-project';
import { EditStageProjectComponent } from '../projects/display-projects/edit-stage-project/edit-stage-project';
import { EditProjectComponent } from '../projects/edit-project/edit-project';
import { ListStageProjectComponent } from './list-stage-project/list-stage-project';
import { GanttStageProjectComponent } from '../projects/display-projects/gantt-stage-project/gantt-stage-project';
import { KanbanStageProjectComponent } from './kanban-stage-project/kanban-stage-project';
import { ManageProjectMembersComponent } from '../../admin/employees/manage-project-members/manage-project-members';
import { ConfirmModalComponent } from '../../shared/utils/modal-validation/modal-validation';

// DETAIL FOR PROJECT AND DISPLAY EACH METHOD TO VISUALISE IT KANBAN LSIT GANTT
@Component({
  selector: 'app-detail-project',
  imports: [
    CommonModule,
    FormsModule,
    AddStageProjectComponent,
    EditStageProjectComponent,
    ListStageProjectComponent,
    GanttStageProjectComponent,
    KanbanStageProjectComponent,
    ManageProjectMembersComponent,
    ConfirmModalComponent,
    EditProjectComponent,
  ],
  templateUrl: './detail-project.html'
})

export class DetailProjectComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  user = inject(User); // Made public for template access - for admin check
  private taskService = inject(TaskService);
  private externalLinkService = inject(ExternalLinkService);
  private clientService = inject(ClientService);
  private timeEntriesService = inject(TimeEntriesService);

  @ViewChild('projectNameInput') projectNameInput?: ElementRef<HTMLInputElement>;

  projectId!: string;
  loading = true;
  accessDenied = false; // Track if user doesn't have access
  currentUserId: string | null = null; // Store current user ID for permission checks
  private loadedTenantId: string | null = null; // Track loaded tenant to avoid redundant calls

  project?: Project;
  projectStages: ProjectStage[] = [];
  employees: UserModel[] = [];
  tasks: Task[] = [];
  tasksByStage: Record<string, Task[]> = {};

  // Phases are never hidden: a phase with no open task still has to be reachable
  // to add one to it. Only the task list inside each phase is filtered.
  phaseStatusFilter: 'all' | 'open' | 'done' = 'all';
  // Task filter (Open / All / Done). Default 'open' so finished work stays out of the way.
  taskStatusFilter: 'all' | 'open' | 'done' = 'open';

  /** Total encoded hours (from time entries with matching projectName). */
  encodedHours = 0;
  encodedHoursLoaded = false;
  /** Per-stage encoded hours map: stageId → hours */
  encodedHoursByStage: Record<string, number> = {};


  viewMode: 'list' | 'gantt' | 'kanban' = 'list';
  private viewModeInitialized = false; // Track if viewMode has been initialized

  showAddStageModal = false;
  showEditStageModal = false;
  editingStage: ProjectStage | null = null;
  showManageMembers = false;
  showEditProjectModal = false;
  showTeamList = false; // collapsed by default
  showProjectDetails = false; // collapsed project info panel


  // Project name editing
  isEditingProjectName = false;
  editedProjectName = '';

  // Project links management
  showAddLinkForm = false;
  newLinkUrl = '';
  newLinkType: 'Drive' | 'Other' = 'Drive';
  newLinkName = 'Drive';

  // Client linking
  clients: ClientModel[] = [];
  showClientDropdown = false;
  selectedClientId = '';
  // Searchable combobox
  clientSearchQuery = '';
  showClientResults = false;
  filteredClientsForSearch: ClientModel[] = [];
  /** Optimistic client shown before backend confirms (cleared on next project refresh) */
  localClient: { id: string; name: string } | null = null;
  // Confirm modal for unlink
  showUnlinkConfirm = false;

  // Get project manager user IDs for permission checking
  get projectManagerUserIds(): string[] {
    return this.project?.managers?.map(m => m.userId).filter(Boolean) || [];
  }

  // Check if current user can manage project (admin or project manager)
  canManageProject(): boolean {
    // Admin can always manage
    if (this.user.admin()) {
      return true;
    }

    // Check if current user is a project manager
    if (!this.currentUserId) {
      return false;
    }

    return this.projectManagerUserIds.includes(this.currentUserId);
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('id')!;

    // Get current user ID for permission checks
    this.userService.currentUser$.subscribe(user => {
      this.currentUserId = user?.id || null;
    });

    // Open the task detail modal whenever a taskId query param is present.
    // Existing ?taskId= links (notifications, dashboard) now redirect to the task
    // page, which resolves the task itself — so no waiting for the store here.
    this.route.queryParams.subscribe(params => {
      const taskId = params['taskId'];
      if (taskId) this.openTaskDetailModal(taskId);
    });

    // React to switching to a different project without a full component reload
    // (Angular reuses this component across /projects/:id navigations).
    this.route.paramMap.subscribe(pm => {
      const id = pm.get('id');
      if (!id || id === this.projectId) return;
      this.projectId = id;
      this.accessDenied = false;
      this.encodedHoursLoaded = false;
      this.encodedHours = 0;
      this.encodedHoursByStage = {};
      this.loadedTenantId = null;
      this.applyProject();
      this.applyStages();
      this.listenEmployees();
      this.checkProjectAccess();
    });

    this.listenProject();
    this.listenStages();
    this.listenTasks();
    this.listenEmployees();
    this.listenSettings();
    // Note: Don't call userService.listUsers() here as it can interfere with tenant filtering

    // Load clients for the client-link dropdown
    this.clientService.clients$.subscribe(clients => {
      this.clients = clients;
    });
    this.clientService.init();

    // Note: Real-time updates are now handled by the services globally
    // No need for component-level polling

    // Check project access after data loads
    this.checkProjectAccess();

    this.loading = false;
  }


  // Latest raw snapshots from the services, re-filtered whenever the project changes.
  private allProjectsRaw: Project[] = [];
  private allStagesRaw: ProjectStage[] = [];

  // GET DATAS FROM SERVICES
  private listenProject() {
    this.projectService.projects$.subscribe(projects => {
      this.allProjectsRaw = projects;
      this.applyProject();
    });
  }

  /** Picks the current project from the latest snapshot (re-runnable on project switch). */
  private applyProject() {
    const found = this.allProjectsRaw.find(p => p.projectId === this.projectId);
    if (found) {
      // Merge localClient for optimistic display while backend confirms
      this.project = this.localClient && !found.client
        ? { ...found, clientId: this.localClient.id, client: this.localClient }
        : found;
      // Load encoded hours once we know the projectId
      if (!this.encodedHoursLoaded && found.projectId) {
        this.encodedHoursLoaded = true;
        this.loadEncodedHours(found.projectId, found.projectName);
      }
    } else {
      this.project = found;
    }
  }

  private listenStages() {
    this.projectService.projectsStage$.subscribe(stages => {
      this.allStagesRaw = stages;
      this.applyStages();
    });
  }

  /** Filters/sorts stages for the current project (re-runnable on project switch). */
  private applyStages() {
    this.projectStages = this.allStagesRaw
      .filter(s => s.projectId === this.projectId)
      .sort((a, b) => this.compareStages(a, b));
    this.rebuildKanban();
  }

  private listenTasks() {
    this.taskService.tasks$.subscribe(tasks => {
      this.tasks = tasks;
      this.rebuildKanban();
    });
  }

  private async listenEmployees() {
    // Wait for first non-empty projects emission
    const projects = await firstValueFrom(
      this.projectService.projects$.pipe(filter(p => p.length > 0))
    );

    const currentProject = projects.find(p => p.projectId === this.projectId);

    if (currentProject?.tenantId) {
      console.log(`📥 [DetailProject] Loading users for tenant: ${currentProject.tenantId}`);
      try {
        // Keep archived users here: `employees` is passed down to the task
        // pickers and avatar displays, which filter archived out themselves
        // while still showing anyone already assigned.
        this.employees = await this.userService.getUsersByTenantId(currentProject.tenantId);
        this.loadedTenantId = currentProject.tenantId;
        console.log(`✅ [DetailProject] Loaded ${this.employees.length} users for tenant`);
      } catch (error) {
        console.error('Failed to load tenant users:', error);
        this.employees = [];
      }
    } else {
      console.log(`⚠️ [DetailProject] No tenantId, loading all users`);
      // Load all users once
      const allUsers = await new Promise<UserModel[]>((resolve) => {
        const sub = this.userService.users$.subscribe(users => {
          resolve(users);
          sub.unsubscribe();
        });
      });
      this.employees = allUsers;
      this.loadedTenantId = 'NO_TENANT';
    }
  }

  private listenSettings() {
    this.userService.settings$.subscribe(settings => {
      // Only set viewMode on first initialization, not on subsequent updates
      if (!this.viewModeInitialized && settings?.displayModeForProject) {
        const mode = settings.displayModeForProject.toLowerCase();
        this.viewMode = (mode === 'gantt' || mode === 'list' || mode === 'kanban') ? mode : 'list';
        this.viewModeInitialized = true;
      }
    });
  }

  // KANBAN METHODS
  private rebuildKanban() {
    if (!this.projectStages.length || !this.tasks.length) {
      this.tasksByStage = {};
      return;
    }
    const stageIds = this.getFilteredStages().map(s => s.projectStageId);
    const tasksForProject = this.getFilteredTasks().filter(t => stageIds.includes(t.projectStageId));
    this.buildTasksByStage(tasksForProject);
  }

  /** Returns stages filtered by the phase status filter */
  getFilteredStages(): ProjectStage[] {
    if (this.phaseStatusFilter === 'all') return this.projectStages;
    if (this.phaseStatusFilter === 'done') return this.projectStages.filter(s => s.status === 'DONE');
    return this.projectStages.filter(s => s.status !== 'DONE');
  }

  /** Returns tasks filtered by the task status filter */
  getFilteredTasks(): Task[] {
    if (this.taskStatusFilter === 'all') return this.tasks;
    if (this.taskStatusFilter === 'done') return this.tasks.filter(t => t.status === 'DONE');
    return this.tasks.filter(t => t.status !== 'DONE');
  }

  onPhaseStatusFilterChange() {
    this.rebuildKanban();
  }

  onTaskStatusFilterChange() {
    this.rebuildKanban();
  }

  private buildTasksByStage(tasks: Task[]) {
    const map: Record<string, Task[]> = {};
    for (const task of tasks) {
      if (!map[task.projectStageId]) map[task.projectStageId] = [];
      map[task.projectStageId].push(task);
    }
    this.tasksByStage = map;
  }

  onTaskMoved(event: { taskId: string; toStageId: string }) {
    this.taskService.updateTask(event.taskId, { projectStageId: event.toStageId });
  }

  // STAGE METHOID
  updateStage(stageId: string, changes: Partial<ProjectStage>) {
    console.log('updateStage called with:', stageId, changes);
    this.projectService.updateProjectStage(stageId, changes)
      .then(result => console.log('Update result:', result))
      .catch(err => console.error('Update error:', err));
  }

  onStageDeleted(stageId: string) {
    this.projectStages = this.projectStages.filter(
      stage => stage.projectStageId !== stageId
    );
  }

  compareStages(a: ProjectStage, b: ProjectStage): number {
    // Sort ONLY by order field - dates and status should not affect order
    return (a.order ?? 0) - (b.order ?? 0);
  }

  // UTILS
  /** Task creation is a full page now, not a modal — the phase context travels in the URL. */
  openAddTaskModal(stageId: string) {
    this.router.navigate(['/projects', this.projectId, 'phases', stageId, 'tasks', 'new']);
  }


  /**
   * Task details are a full page now. The page resolves the task itself, so this
   * only navigates — no need to have the task loaded here first, which also fixes
   * deep links arriving before the store is populated.
   */
  openTaskDetailModal(taskId: string) {
    this.router.navigate(['/projects', this.projectId, 'tasks', taskId]);
  }

  goBack() {
    this.router.navigate(['/projects']);
  }

  // Get all users who are members or managers of this project
  getProjectMembers(): UserModel[] {
    return this.projectService.getProjectMemberUsers(this.project, this.employees);
  }

  getEmployeesForProjectTasks(): UserModel[] {
    return this.projectService.getEmployeesForProjectTasks(
      this.projectStages,
      this.tasks,
      this.employees
    );
  }

  getEmployeeNamesForTooltip(): string {
    return this.getProjectMembers()
      .slice(3)
      .map(emp => `${emp.firstName} ${emp.name}`)
      .join(', ');
  }

  /**
   * Fused deduplicated team list:
   * 1. project.managers (role: 'manager')
   * 2. project.members (role: 'member')
   * 3. task-assigned employees (role: 'assignee')
   */
  getFusedTeam(): { id: string; firstName: string; lastName: string; role: 'manager' | 'member' | 'assignee' }[] {
    const seen = new Set<string>();
    const result: { id: string; firstName: string; lastName: string; role: 'manager' | 'member' | 'assignee' }[] = [];

    // 1. Managers (archived people are no longer shown as active team)
    for (const m of this.project?.managers ?? []) {
      const id = m.userId;
      if (!id || seen.has(id) || m.user?.isArchived) continue;
      seen.add(id);
      result.push({ id, firstName: m.user?.firstName ?? '', lastName: m.user?.name ?? '', role: 'manager' });
    }

    // 2. Members
    for (const m of this.project?.members ?? []) {
      const id = m.userId;
      if (!id || seen.has(id) || m.user?.isArchived) continue;
      seen.add(id);
      result.push({ id, firstName: m.user?.firstName ?? '', lastName: m.user?.name ?? '', role: 'member' });
    }

    // 3. Task-assigned employees (people with tasks in this project)
    for (const emp of this.getEmployeesForProjectTasks()) {
      if (!emp.id || seen.has(emp.id) || emp.isArchived) continue;
      seen.add(emp.id);
      result.push({ id: emp.id, firstName: emp.firstName ?? '', lastName: emp.name ?? '', role: 'assignee' });
    }

    return result;
  }

  closeManageMembers() {
    this.showManageMembers = false;
  }

  /** Returns a comma-separated tooltip of team members beyond the first 4 */
  getOverflowTeamTooltip(): string {
    return this.getFusedTeam()
      .slice(4)
      .map(p => `${p.firstName} ${p.lastName}`.trim())
      .join(', ');
  }

  getTotalTasksCount(): number {
    return this.tasks.length;
  }

  /** Tasks belonging to this project's stages (unfiltered by status). */
  private getProjectTasks(): Task[] {
    const stageIds = new Set(this.projectStages.map(s => s.projectStageId));
    return this.tasks.filter(t => stageIds.has(t.projectStageId));
  }

  /** Overall completion (%) = done tasks / total tasks across all phases. */
  getAvancement(): number {
    const tasks = this.getProjectTasks();
    if (!tasks.length) return 0;
    const done = tasks.filter(t => t.status === 'DONE').length;
    return Math.round((done / tasks.length) * 100);
  }

  /** Project deadline = latest phase end date (ISO string) or null. */
  getProjectDeadline(): string | null {
    const ends = this.projectStages
      .map(s => s.endDate)
      .filter((d): d is string => !!d)
      .sort();
    return ends.length ? ends[ends.length - 1] : null;
  }

  /** Derived project status from its phases: À venir / Actif / Terminé. */
  getProjectStatusInfo(): { label: string; dot: string; text: string } {
    const stages = this.projectStages;
    if (stages.length && stages.every(s => s.status === 'DONE')) {
      return { label: 'Done', dot: 'bg-emerald-500', text: 'text-emerald-600' };
    }
    if (stages.some(s => s.status === 'INPROGRESS')) {
      return { label: 'Active', dot: 'bg-emerald-500', text: 'text-emerald-600' };
    }
    if (stages.some(s => s.status === 'DONE')) {
      return { label: 'Active', dot: 'bg-emerald-500', text: 'text-emerald-600' };
    }
    return { label: 'Upcoming', dot: 'bg-gray-400', text: 'text-gray-500' };
  }

  /** Initials for a team member avatar. */
  getTeamInitials(p: { firstName: string; lastName: string }): string {
    const f = (p.firstName || '').charAt(0).toUpperCase();
    const l = (p.lastName || '').charAt(0).toUpperCase();
    return (f + l) || '?';
  }

  /** Change the active view and persist the preference. */
  setViewMode(mode: 'list' | 'gantt' | 'kanban') {
    this.viewMode = mode;
  }

  // Project name editing methods
  startEditProjectName() {
    if (this.project) {
      this.editedProjectName = this.project.projectName;
      this.isEditingProjectName = true;
      // Focus the input after Angular renders it
      setTimeout(() => {
        this.projectNameInput?.nativeElement.focus();
        this.projectNameInput?.nativeElement.select();
      });
    }
  }

  saveProjectName() {
    if (this.project && this.editedProjectName.trim() && this.editedProjectName.trim() !== this.project.projectName) {
      this.projectService.updateProject(this.projectId, { projectName: this.editedProjectName.trim() })
        .then(() => {
          console.log('Project name updated');
          this.isEditingProjectName = false;
        })
        .catch(err => {
          console.error('Error updating project name:', err);
          this.isEditingProjectName = false;
        });
    } else {
      this.isEditingProjectName = false;
    }
  }

  cancelEditProjectName() {
    this.isEditingProjectName = false;
    this.editedProjectName = '';
  }

  // Project estimated hours update (direct like phases)
  updateProjectEstimatedHours() {
    if (this.project && this.user.admin()) {
      this.projectService.updateProject(this.projectId, { estimatedHours: this.project.estimatedHours })
        .then(() => console.log('Project estimated hours updated'))
        .catch(err => console.error('Error updating project estimated hours:', err));
    }
  }

  // ON THE FLY CALCULATIONS

  /** Hours allocated to stages (attribué aux phases). */
  getProjectAllocatedHours(): number {
    return this.projectStages.reduce((acc, stage) => acc + (stage.estimatedHours || 0), 0);
  }

  /** Budget remaining = project estimate - sum of stage allocations. */
  getProjectRemainingTime(): number {
    if (!this.project) return 0;
    const projectEst = this.project.estimatedHours || 0;
    const stagesSum = this.getProjectAllocatedHours();
    return projectEst - stagesSum;
  }

  /** Encoded hours vs PROJECT BUDGET difference (not stages — the true max ceiling). */
  getEncodedVsBudgetDiff(): number {
    if (!this.project) return 0;
    return (this.project.estimatedHours || 0) - this.encodedHours;
  }

  /** Sum of all task estimated hours across this project (task-level allocation). */
  getTotalTaskHours(): number {
    const stageIds = this.projectStages.map(s => s.projectStageId);
    return this.tasks
      .filter(t => stageIds.includes(t.projectStageId))
      .reduce((acc, t) => acc + (t.estimatedHours || 0), 0);
  }

  private async loadEncodedHours(projectId: string, projectName?: string) {
    try {
      const entries = await this.timeEntriesService.fetchAllByProjectId(projectId, projectName);
      // Total project encoded hours
      this.encodedHours = entries.reduce((acc, e) => {
        const ms = new Date(e.endTime).getTime() - new Date(e.startTime).getTime();
        return acc + ms / 3_600_000;
      }, 0);
      // Per-stage encoded hours — use stageId if available, else parse from projectName
      const byStage: Record<string, number> = {};
      // Build a lookup: stageName (lowercase) → stageId for this project's stages
      const stagesByName = new Map<string, string>();
      for (const stage of this.projectStages) {
        if (stage.stageName && stage.projectStageId) {
          stagesByName.set(stage.stageName.trim().toLowerCase(), stage.projectStageId);
        }
      }
      for (const e of entries) {
        let resolvedStageId = e.stageId ?? null;
        // Fallback: parse "ProjectName - StageName - TaskName" from projectName
        if (!resolvedStageId && e.projectName) {
          const parts = e.projectName.split(' - ');
          if (parts.length >= 2) {
            resolvedStageId = stagesByName.get(parts[1].trim().toLowerCase()) ?? null;
          }
        }
        if (resolvedStageId) {
          const ms = new Date(e.endTime).getTime() - new Date(e.startTime).getTime();
          byStage[resolvedStageId] = (byStage[resolvedStageId] || 0) + ms / 3_600_000;
        }
      }
      this.encodedHoursByStage = byStage;
    } catch (err) {
      console.error('[DetailProject] Failed to load encoded hours:', err);
    }
  }

  getStageRemainingTime(stageId: string): number {
    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    if (!stage) return 0;
    const stageEst = stage.estimatedHours || 0;
    const tasksSum = (this.tasksByStage[stageId] || []).reduce((acc, task) => acc + (task.estimatedHours || 0), 0);
    return stageEst - tasksSum;
  }

  // Project links management methods
  toggleAddLink() {
    this.showAddLinkForm = !this.showAddLinkForm;
    if (!this.showAddLinkForm) {
      this.newLinkUrl = '';
      this.newLinkType = 'Drive';
      this.newLinkName = 'Drive';
    }
  }

  onLinkTypeChange() {
    if (this.newLinkType === 'Drive') {
      this.newLinkName = 'Drive';
    } else {
      this.newLinkName = '';
    }
  }

  addLink() {
    if (!this.project || !this.newLinkUrl.trim() || !this.newLinkName.trim()) {
      console.log('❌ [addLink] Validation failed - missing project, URL or name');
      return;
    }

    // Basic URL validation
    try {
      new URL(this.newLinkUrl.trim());
    } catch {
      alert('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    const currentLinks = this.project.links || [];
    // Create structured link object
    const newLink = {
      name: this.newLinkName.trim(),
      url: this.newLinkUrl.trim()
    };
    const updatedLinks = [...currentLinks, newLink];

    console.log('📎 [addLink] Current links:', currentLinks);
    console.log('📎 [addLink] New link:', newLink);
    console.log('📎 [addLink] Updated links:', updatedLinks);
    console.log('📎 [addLink] Updated links JSON:', JSON.stringify(updatedLinks));

    this.projectService.updateProject(this.projectId, { links: updatedLinks })
      .then(() => {
        console.log('✅ [addLink] Link added successfully');
        this.newLinkUrl = '';
        this.newLinkType = 'Drive';
        this.newLinkName = 'Drive';
        this.showAddLinkForm = false;
      })
      .catch(err => {
        console.error('❌ [addLink] Error adding link:', err);
        alert('Failed to add link. Please try again.');
      });
  }

  removeLink(index: number) {
    if (!this.project || !confirm('Are you sure you want to remove this link?')) {
      return;
    }

    const currentLinks = this.project.links || [];
    const updatedLinks = currentLinks.filter((_, i) => i !== index);

    this.projectService.updateProject(this.projectId, { links: updatedLinks })
      .then(() => {
        console.log('Link removed successfully');
      })
      .catch(err => {
        console.error('Error removing link:', err);
        alert('Failed to remove link. Please try again.');
      });
  }

  cancelAddLink() {
    this.showAddLinkForm = false;
    this.newLinkUrl = '';
    this.newLinkType = 'Drive';
    this.newLinkName = 'Drive';
  }

  // Client linking methods
  openClientDropdown() {
    this.selectedClientId = this.project?.clientId || '';
    this.showClientDropdown = true;
  }

  cancelClientDropdown() {
    this.showClientDropdown = false;
    this.selectedClientId = '';
  }

  async linkClient() {
    if (!this.selectedClientId) return;
    try {
      await this.projectService.updateProject(this.projectId, { clientId: this.selectedClientId });
      this.showClientDropdown = false;
      this.selectedClientId = '';
    } catch (err) {
      console.error('Error linking client:', err);
      alert('Failed to link client. Please try again.');
    }
  }

  unlinkClient() {
    this.showUnlinkConfirm = true;
  }

  async handleUnlinkConfirm(result: 'ok' | 'cancel') {
    this.showUnlinkConfirm = false;
    if (result !== 'ok') return;
    const previousClient = this.project?.client;
    this.localClient = null;
    if (this.project) {
      this.project = { ...this.project, clientId: undefined, client: undefined };
    }
    this.clientSearchQuery = '';
    this.showClientResults = false;
    this.filteredClientsForSearch = [];
    try {
      await this.projectService.updateProject(this.projectId, { clientId: undefined });
    } catch (err) {
      if (previousClient) { this.localClient = previousClient; }
      if (this.project && previousClient) {
        this.project = { ...this.project, clientId: previousClient.id, client: previousClient };
      }
      console.error('Error unlinking client:', err);
      alert('Failed to unlink client. Please try again.');
    }
  }

  filterClientsForSearch() {
    const q = this.clientSearchQuery.toLowerCase().trim();
    this.filteredClientsForSearch = q
      ? this.clients.filter(c => c.name.toLowerCase().includes(q))
      : this.clients.slice(0, 8);
    this.showClientResults = true;
  }

  async selectClientFromSearch(client: ClientModel) {
    this.selectedClientId = client.id;
    this.clientSearchQuery = '';
    this.showClientResults = false;
    this.filteredClientsForSearch = [];

    // Optimistic local display while backend confirms
    this.localClient = { id: client.id, name: client.name };

    // Show the client pill immediately
    if (this.project) {
      this.project = { ...this.project, clientId: client.id, client: { id: client.id, name: client.name } };
    }

    try {
      await this.projectService.updateProject(this.projectId, { clientId: client.id });
    } catch (err) {
      console.error('Error linking client:', err);
      this.localClient = null;
      if (this.project) {
        this.project = { ...this.project, clientId: undefined, client: undefined };
      }
      alert('Failed to link client. Please try again.');
    }
  }

  closeClientResults() {
    // small delay so click on result registers first
    setTimeout(() => { this.showClientResults = false; }, 150);
  }

  /** Called when the admin picks a client from the inline select — saves immediately */
  onClientSelectChange() {
    if (this.selectedClientId) {
      this.linkClient();
    }
  }


  // Parse link format: ProjectLink object or legacy string
  parseLinkName(link: any): string {
    // New format: object with name property
    if (typeof link === 'object' && link?.name) {
      return link.name;
    }
    // Legacy format: string with |||
    if (typeof link === 'string' && link.includes('|||')) {
      return link.split('|||')[0];
    }
    // Fallback for old format (just URL)
    return this.getLinkDisplayName(link);
  }

  parseLinkUrl(link: any): string {
    // New format: object with url property
    if (typeof link === 'object' && link?.url) {
      return link.url;
    }
    // Legacy format: string with |||
    if (typeof link === 'string' && link.includes('|||')) {
      return link.split('|||')[1];
    }
    // Fallback for old format (just URL)
    return link;
  }

  getLinkDisplayName(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  }

  // Open external link (works in both web and Tauri)
  openExternalLink(url: string, event?: Event): void {
    this.externalLinkService.openExternal(url, event);
  }

  // Check if user has access to this project
  private async checkProjectAccess() {
    // Wait for project and tasks to load
    setTimeout(async () => {
      // Admin can always access
      if (this.user.admin()) {
        return;
      }

      // Get current user ID
      const currentUser = await new Promise<UserModel | null>((resolve) => {
        const subscription = this.userService.currentUser$.subscribe(user => {
          resolve(user);
          subscription.unsubscribe();
        });
      });

      if (!currentUser?.id) {
        console.warn('No current user found, redirecting to projects list');
        this.accessDenied = true;
        setTimeout(() => this.router.navigate(['/projects']), 3000);
        return;
      }

      const currentUserId = currentUser.id;

      // Check if user is a project manager
      const isProjectManager = this.project?.managers?.some(
        manager => manager.userId === currentUserId
      );

      if (isProjectManager) {
        return; // Manager has access
      }

      // Check if user is a project member
      const isProjectMember = this.project?.members?.some(
        member => member.userId === currentUserId
      );

      if (isProjectMember) {
        return; // Member has access
      }

      // Check if user is assigned to any task in this project
      const hasAssignedTask = this.tasks.some(task =>
        task.projectStageId &&
        this.projectStages.some(stage => stage.projectStageId === task.projectStageId) &&
        task.employeeId &&
        Array.isArray(task.employeeId) &&
        task.employeeId.includes(currentUserId)
      );

      if (!hasAssignedTask) {
        console.warn('User does not have access to this project');
        this.accessDenied = true;
        setTimeout(() => this.router.navigate(['/projects']), 3000);
      }
    }, 1000); // Wait 1 second for data to load
  }
}
